import type { LocalPoint } from "@excalidraw/math/types";

import { mulberry32 } from "./random";

import type { BrushParams } from "./types";

export type StampPlacement = {
  x: number;
  y: number;
  size: number;
  alpha: number;
  rotation: number;
};

export const MAX_STAMPS_PER_STROKE = 4000;

/** simple exponential moving average smoothing, strength = streamline */
const smooth = (
  points: readonly LocalPoint[],
  streamline: number,
): { x: number; y: number }[] => {
  if (points.length < 3 || streamline <= 0) {
    return points.map(([x, y]) => ({ x, y }));
  }
  const k = 1 - Math.min(0.9, streamline) * 0.75;
  const out = [{ x: points[0][0], y: points[0][1] }];
  for (let i = 1; i < points.length; i++) {
    const prev = out[i - 1];
    out.push({
      x: prev.x + (points[i][0] - prev.x) * k,
      y: prev.y + (points[i][1] - prev.y) * k,
    });
  }
  // keep the true endpoint so strokes reach the pointer
  out[out.length - 1] = {
    x: points[points.length - 1][0],
    y: points[points.length - 1][1],
  };
  return out;
};

export const computeStamps = (
  points: readonly LocalPoint[],
  pressures: readonly number[],
  params: BrushParams,
  size: number,
  seed: number,
  window?: { start: number; end: number },
): StampPlacement[] => {
  if (points.length === 0 || size <= 0) {
    return [];
  }
  const rng = mulberry32(seed);
  const pressureAt = (t: number): number => {
    if (pressures.length === 0) {
      return 0.5;
    }
    const idx = t * (pressures.length - 1);
    const lo = Math.floor(idx);
    const hi = Math.min(pressures.length - 1, lo + 1);
    return pressures[lo] + (pressures[hi] - pressures[lo]) * (idx - lo);
  };

  const pts = smooth(points, params.streamline);

  // cumulative arc length
  const cum: number[] = [0];
  for (let i = 1; i < pts.length; i++) {
    cum.push(
      cum[i - 1] + Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y),
    );
  }
  const total = cum[cum.length - 1];

  const makeStamp = (
    x: number,
    y: number,
    t: number,
    angle: number,
  ): StampPlacement => {
    const pressure = pressureAt(t);
    // taper: ramp size at both ends over `taper` fraction of the path
    const taperT =
      params.taper > 0
        ? Math.min(1, Math.min(t, 1 - t) / Math.max(0.001, params.taper))
        : 1;
    const pSize = 1 - params.sizePressure * (1 - pressure * 2 * 0.999);
    const stampSize = Math.max(
      0.5,
      size * Math.max(0.05, pSize) * (0.35 + 0.65 * taperT),
    );
    const pAlpha = 1 - params.opacityPressure * (1 - pressure * 2 * 0.999);
    const jitterA = 1 - params.opacityJitter * rng();
    const alpha = Math.max(0.02, Math.min(1, pAlpha)) * jitterA;
    const scatterAmt = (rng() * 2 - 1) * params.scatter * size;
    const nx = -Math.sin(angle);
    const ny = Math.cos(angle);
    const rotation =
      (params.followPath ? angle : 0) +
      (rng() * 2 - 1) * params.rotationJitter * Math.PI;
    return {
      x: x + nx * scatterAmt,
      y: y + ny * scatterAmt,
      size: stampSize,
      alpha,
      rotation,
    };
  };

  if (total === 0) {
    // tap: single stamp at the point
    return [makeStamp(pts[0].x, pts[0].y, 0.5, 0)];
  }

  let step = Math.max(0.25, params.spacing * size);
  if (total / step > MAX_STAMPS_PER_STROKE - 1) {
    // ponytail: cap, spacing degrades on absurdly long strokes
    step = total / (MAX_STAMPS_PER_STROKE - 1);
  }

  const startLen = (window?.start ?? 0) * total;
  const endLen = (window?.end ?? 1) * total;

  const stamps: StampPlacement[] = [];
  let segIdx = 0;
  for (let d = 0; d <= total + 1e-6; d += step) {
    // NOTE: rng must advance identically regardless of window so that a
    // partially-drawn (animating) stroke matches the final stroke exactly.
    while (segIdx < cum.length - 2 && cum[segIdx + 1] < d) {
      segIdx++;
    }
    const segLen = cum[segIdx + 1] - cum[segIdx] || 1;
    const f = (d - cum[segIdx]) / segLen;
    const x = pts[segIdx].x + (pts[segIdx + 1].x - pts[segIdx].x) * f;
    const y = pts[segIdx].y + (pts[segIdx + 1].y - pts[segIdx].y) * f;
    const angle = Math.atan2(
      pts[segIdx + 1].y - pts[segIdx].y,
      pts[segIdx + 1].x - pts[segIdx].x,
    );
    const stamp = makeStamp(x, y, d / total, angle);
    if (d >= startLen - 1e-6 && d <= endLen + 1e-6) {
      stamps.push(stamp);
    }
  }
  return stamps;
};
